import React, { useEffect, useMemo, useState } from "react";
import {
  Box,
  Button,
  Checkbox,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  MenuItem,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import axios from "axios";

const defaultDraft = {
  id: null,
  title: "",
  paymentPolicyId: null,
  requireUpfrontPayment: false,
  upfrontMode: "full",
  depositType: "percent",
  depositValue: "",
  paymentDueOnSign: true,
  nextTermId: "",
  isCreditHold: false,
};

export default function PaymentTermsTab({ instanceUniqueId }) {
  const [terms, setTerms] = useState([]);
  const [loading, setLoading] = useState(false);
  const [open, setOpen] = useState(false);
  const [draft, setDraft] = useState(defaultDraft);
  const [instanceId, setInstanceId] = useState(null);

  const SERVER = process.env.REACT_APP_NODEJS_SERVER_URL;
  const headers = { headers: { accessToken: localStorage.getItem("accessToken") } };

  const fetchInstanceId = async () => {
    if (!instanceUniqueId) return;
    const res = await axios.get(
      `${SERVER}/instances/byinstanceUniqueId/${instanceUniqueId}`,
      headers
    );
    setInstanceId(res.data?.id || null);
  };

  const fetchTerms = async (targetInstanceId = instanceId) => {
    if (!targetInstanceId) return;
    setLoading(true);
    try {
      const res = await axios.get(
        `${SERVER}/instances/${targetInstanceId}/terms`,
        headers
      );
      setTerms(res.data.terms || []);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchInstanceId();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [instanceUniqueId]);

  useEffect(() => {
    if (instanceId) fetchTerms(instanceId);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [instanceId]);

  const openCreate = () => {
    setDraft(defaultDraft);
    setOpen(true);
  };

  const openEdit = (term) => {
    const policy = term.paymentPolicy || null;
    setDraft({
      id: term.id,
      title: term.name || "",
      paymentPolicyId: term.isCreditHold ? null : (term.paymentPolicyId || null),
      requireUpfrontPayment: !term.isCreditHold && Boolean(policy?.requireUpfrontPayment),
      upfrontMode: policy?.upfrontMode || "full",
      depositType: policy?.depositType || "percent",
      depositValue: policy?.depositValue ?? "",
      paymentDueOnSign: policy?.paymentDueOnSign ?? true,
      nextTermId: policy?.nextTermId ?? "",
      isCreditHold: Boolean(term.isCreditHold),
    });
    setOpen(true);
  };

  const save = async () => {
    const payload = {
      title: draft.title.trim(),
      paymentPolicyId: draft.isCreditHold ? null : draft.paymentPolicyId,
      isCreditHold: Boolean(draft.isCreditHold),
      requireUpfrontPayment: draft.isCreditHold ? false : draft.requireUpfrontPayment,
      upfrontMode: !draft.isCreditHold && draft.requireUpfrontPayment ? draft.upfrontMode : null,
      depositType:
        !draft.isCreditHold && draft.requireUpfrontPayment && draft.upfrontMode === "deposit"
          ? draft.depositType
          : null,
      depositValue:
        !draft.isCreditHold && draft.requireUpfrontPayment && draft.upfrontMode === "deposit"
          ? draft.depositValue
          : null,
      paymentDueOnSign: !draft.isCreditHold && draft.requireUpfrontPayment ? draft.paymentDueOnSign : null,
      nextTermId: !draft.isCreditHold && draft.requireUpfrontPayment ? draft.nextTermId : null,
    };
    if (!payload.title || !instanceId) return;

    if (draft.id) {
      await axios.put(
        `${SERVER}/instances/${instanceId}/terms/${draft.id}`,
        payload,
        headers
      );
    } else {
      await axios.post(`${SERVER}/instances/${instanceId}/terms`, payload, headers);
    }

    setOpen(false);
    fetchTerms(instanceId);
  };

  const remove = async (term) => {
    if (!instanceId) return;
    await axios.delete(`${SERVER}/instances/${instanceId}/terms/${term.id}`, headers);
    fetchTerms(instanceId);
  };

  const isValid = useMemo(() => draft.title.trim().length > 0, [draft]);

  return (
    <Box sx={{ p: 2 }}>
      <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 2 }}>
        <Box>
          <Typography variant="h6" sx={{ fontWeight: 800 }}>
            Payment Terms
          </Typography>
          <Typography variant="body2" sx={{ opacity: 0.8 }}>
            Manage the payment terms available for this instance.
          </Typography>
        </Box>

        <Button variant="contained" onClick={openCreate}>
          Add Term
        </Button>
      </Box>

      <Table size="small">
        <TableHead>
          <TableRow>
            <TableCell>Title</TableCell>
            <TableCell align="right">Actions</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {(terms || []).map((term) => (
            <TableRow key={term.id} hover>
              <TableCell sx={{ fontWeight: 700 }}>
                <Stack direction="row" spacing={1} alignItems="center">
                  <span>{term.name}</span>
                  {term.isCreditHold ? (
                    <Tooltip title="Customers assigned this payment term cannot be selected for new projects/orders.">
                      <Typography
                        component="span"
                        sx={{
                          fontSize: 11,
                          fontWeight: 700,
                          px: 1,
                          py: 0.25,
                          borderRadius: 1,
                          border: "1px solid",
                          borderColor: "warning.main",
                          bgcolor: "warning.light",
                          color: "warning.contrastText",
                        }}
                      >
                        Credit Hold
                      </Typography>
                    </Tooltip>
                  ) : term.paymentPolicy?.requireUpfrontPayment ? (
                    <Tooltip title="Requires upfront payment on agreement.">
                      <Typography
                        component="span"
                        sx={{
                          fontSize: 11,
                          fontWeight: 700,
                          px: 1,
                          py: 0.25,
                          borderRadius: 1,
                          border: "1px solid",
                          borderColor: "divider",
                          bgcolor: "background.default",
                        }}
                      >
                        Payment required
                      </Typography>
                    </Tooltip>
                  ) : null}
                </Stack>
              </TableCell>
              <TableCell align="right">
                <IconButton onClick={() => openEdit(term)} size="small">
                  <EditOutlinedIcon fontSize="small" />
                </IconButton>
                <IconButton onClick={() => remove(term)} size="small">
                  <DeleteOutlineIcon fontSize="small" />
                </IconButton>
              </TableCell>
            </TableRow>
          ))}
          {!loading && (terms || []).length === 0 && (
            <TableRow>
              <TableCell colSpan={2} sx={{ py: 4, textAlign: "center", opacity: 0.7 }}>
                No payment terms yet. Add your first one.
              </TableCell>
            </TableRow>
          )}
        </TableBody>
      </Table>

      <Dialog open={open} onClose={() => setOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ fontWeight: 800 }}>
          {draft.id ? "Edit Payment Term" : "Create Payment Term"}
        </DialogTitle>
      <DialogContent sx={{ display: "grid", gap: 2, pt: 1 }}>
        <TextField
          label="Title"
          value={draft.title}
          onChange={(e) => setDraft((prev) => ({ ...prev, title: e.target.value }))}
          placeholder="Net 30"
        />
        <Box sx={{ display: "grid", gap: 1 }}>
          <Stack direction="row" spacing={1} alignItems="center">
            <Checkbox
              checked={draft.requireUpfrontPayment}
              onChange={(e) =>
                setDraft((prev) => ({
                  ...prev,
                  requireUpfrontPayment: e.target.checked,
                  isCreditHold: e.target.checked ? false : prev.isCreditHold,
                }))
              }
            />
            <Box>
              <Typography sx={{ fontWeight: 600 }}>Require upfront payment</Typography>
              <Typography variant="caption" sx={{ opacity: 0.7 }}>
                Use this for new-customer terms to force payment before approval.
              </Typography>
            </Box>
          </Stack>
          <Stack direction="row" spacing={1} alignItems="center">
            <Checkbox
              checked={draft.isCreditHold}
              onChange={(e) =>
                setDraft((prev) => ({
                  ...prev,
                  isCreditHold: e.target.checked,
                  requireUpfrontPayment: e.target.checked ? false : prev.requireUpfrontPayment,
                  upfrontMode: e.target.checked ? "full" : prev.upfrontMode,
                  depositType: e.target.checked ? "percent" : prev.depositType,
                  depositValue: e.target.checked ? "" : prev.depositValue,
                  paymentDueOnSign: e.target.checked ? true : prev.paymentDueOnSign,
                  nextTermId: e.target.checked ? "" : prev.nextTermId,
                  paymentPolicyId: e.target.checked ? null : prev.paymentPolicyId,
                }))
              }
            />
            <Box>
              <Typography sx={{ fontWeight: 600 }}>Credit Hold</Typography>
              <Typography variant="caption" sx={{ opacity: 0.7 }}>
                Use this to prevent this customer term from being selected for new projects/orders until the hold is removed.
              </Typography>
            </Box>
          </Stack>
          {draft.requireUpfrontPayment && !draft.isCreditHold && (
            <Box sx={{ display: "grid", gap: 1.5, pl: 5 }}>
              <TextField
                select
                label="Upfront payment type"
                value={draft.upfrontMode}
                onChange={(e) =>
                  setDraft((prev) => ({ ...prev, upfrontMode: e.target.value }))
                }
              >
                <MenuItem value="full">Full payment</MenuItem>
                <MenuItem value="deposit">Deposit</MenuItem>
              </TextField>
              {draft.upfrontMode === "deposit" && (
                <Stack direction={{ xs: "column", sm: "row" }} spacing={1.5}>
                  <TextField
                    select
                    label="Deposit type"
                    value={draft.depositType}
                    onChange={(e) =>
                      setDraft((prev) => ({ ...prev, depositType: e.target.value }))
                    }
                    sx={{ minWidth: 160 }}
                  >
                    <MenuItem value="percent">Percent</MenuItem>
                    <MenuItem value="fixed">Fixed</MenuItem>
                  </TextField>
                  <TextField
                    label={draft.depositType === "fixed" ? "Deposit amount" : "Deposit percent"}
                    value={draft.depositValue}
                    onChange={(e) =>
                      setDraft((prev) => ({ ...prev, depositValue: e.target.value }))
                    }
                    type="number"
                    inputProps={{ min: 0, step: "0.01" }}
                  />
                </Stack>
              )}
              <Stack direction="row" spacing={1} alignItems="center">
                <Checkbox
                  checked={draft.paymentDueOnSign}
                  onChange={(e) =>
                    setDraft((prev) => ({ ...prev, paymentDueOnSign: e.target.checked }))
                  }
                />
                <Typography variant="body2">Payment due on sign</Typography>
              </Stack>
              <TextField
                select
                label="Next term after payment"
                value={draft.nextTermId}
                onChange={(e) =>
                  setDraft((prev) => ({ ...prev, nextTermId: e.target.value }))
                }
                helperText="Auto-assign this term to the customer after successful payment."
              >
                <MenuItem value="">No auto-change</MenuItem>
                {(terms || []).map((term) => (
                  <MenuItem key={term.id} value={term.id}>
                    {term.name}
                  </MenuItem>
                ))}
              </TextField>
            </Box>
          )}
        </Box>
      </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setOpen(false)}>Cancel</Button>
          <Button variant="contained" onClick={save} disabled={!isValid}>
            Save
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
